import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, ChevronDown, LogOut, Settings, Shield, FileText, HeadphonesIcon, Handshake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';

export const Header = () => {
  const { user, logout, isAdmin, isSuperAdmin, isPremium } = useAuth();
  const navigate = useNavigate();

  const handleSupport = () => {
    window.location.href = 'mailto:dking@ethicalwiki.com?subject=Handshake Premium Support';
  };

  return (
    <header className="bg-white border-b border-[#a2a9b1] sticky top-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="logo-link">
            <Handshake className="h-8 w-8 text-[#36c]" />
            <span className="font-serif text-xl font-bold text-[#202122] tracking-tight">Handshake</span>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-3">
            <Link to="/create">
              <Button variant="outline" className="border-[#a2a9b1] text-[#36c] hover:bg-[#eaecf0] hover:text-[#202122] font-medium" data-testid="create-page-btn">
                Create Page
              </Button>
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-[#202122] hover:bg-[#eaecf0]" data-testid="user-menu-btn">
                    <div className="w-8 h-8 rounded-full bg-[#36c] flex items-center justify-center text-white text-sm font-medium">
                      {user.username[0].toUpperCase()}
                    </div>
                    <span className="hidden sm:block text-[#54595d]">{user.username}</span>
                    <ChevronDown className="h-4 w-4 text-[#54595d]" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border-[#a2a9b1]">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-[#202122]">{user.username}</p>
                    <p className="text-xs text-[#54595d]">{user.email}</p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#eaecf0] text-[#36c] mt-1">
                      {user.role}
                    </span>
                  </div>
                  <DropdownMenuSeparator className="bg-[#c8ccd1]" />
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-[#36c] hover:text-[#202122] focus:text-[#202122] focus:bg-[#eaecf0] cursor-pointer" data-testid="profile-menu-item">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  {isAdmin() && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="text-[#36c] hover:text-[#202122] focus:text-[#202122] focus:bg-[#eaecf0] cursor-pointer" data-testid="admin-menu-item">
                      <Shield className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin() && (
                    <DropdownMenuItem onClick={() => navigate('/admin/config')} className="text-[#36c] hover:text-[#202122] focus:text-[#202122] focus:bg-[#eaecf0] cursor-pointer" data-testid="config-menu-item">
                      <Settings className="mr-2 h-4 w-4" />
                      Configuration
                    </DropdownMenuItem>
                  )}
                  {isPremium() && (
                    <DropdownMenuItem onClick={handleSupport} className="text-[#36c] hover:text-[#202122] focus:text-[#202122] focus:bg-[#eaecf0] cursor-pointer" data-testid="support-menu-item">
                      <HeadphonesIcon className="mr-2 h-4 w-4" />
                      Support
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#c8ccd1]" />
                  <DropdownMenuItem onClick={() => navigate('/terms')} className="text-[#36c] hover:text-[#202122] focus:text-[#202122] focus:bg-[#eaecf0] cursor-pointer" data-testid="terms-menu-item">
                    <FileText className="mr-2 h-4 w-4" />
                    Terms of Use
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#c8ccd1]" />
                  <DropdownMenuItem onClick={logout} className="text-[#d33] hover:text-[#b32424] focus:text-[#b32424] focus:bg-[#fee7e6] cursor-pointer" data-testid="logout-btn">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" className="text-[#36c] hover:text-[#202122] hover:bg-[#eaecf0]" data-testid="login-btn">Log in</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#36c] text-white hover:bg-[#2a4b8d] font-medium" data-testid="register-btn">Sign up</Button>
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#f8f9fa] text-[#202122] mt-auto border-t border-[#a2a9b1]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Handshake className="h-7 w-7 text-[#36c]" />
              <span className="font-serif text-lg font-bold tracking-tight">Handshake</span>
            </div>
            <p className="text-sm text-[#54595d]">The free encyclopedia for business</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-medium mb-4 text-[#202122] text-sm">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-[#36c] hover:underline">Home</Link></li>
              <li><Link to="/create" className="text-[#36c] hover:underline">Create Page</Link></li>
              <li><Link to="/search" className="text-[#36c] hover:underline">Search</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-medium mb-4 text-[#202122] text-sm">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/terms" className="text-[#36c] hover:underline">Terms of Use</Link></li>
              <li><Link to="/report" className="text-[#36c] hover:underline">Report Violation</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-[#c8ccd1] mt-8 pt-6 text-center text-xs text-[#54595d]">
          <p>© {new Date().getFullYear()} Handshake. Content is available under CC BY-SA 4.0 unless otherwise noted.</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
